import React from "react";
import { DispatchProp } from "react-redux";
import { includes, trim } from "../../utils/lodash";
import { ChoiceListControl } from "./ChoiceListControl";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import { QuestionType } from "../../models/enums/QuestionType";
import { ChoiceModel } from "../../models/ChoiceModel";
import { PhoneRegularExpression, EmailRegularExpression } from "../../consts";
import { CaseMode } from "../../models/enums/CaseMode";
import {
    getSettingNumber,
    getSettingBoolean,
    getSettingString,
    hasSetting,
    getSettingEnum,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty, isNullOrNaN, removeHtmlTags } from "../../utils";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { formatNumeric, getNumeral, decimalSeparator } from "../../utils/Number";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { translate } from "../../models/utilities/translationUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import { shouldAutoFormat, getSoftPromptCode } from "../../models/utilities/questionUtilities";
import TextAnswerRenderer from "../renderers/v2/TextAnswerRenderer";
import { respondentSetPhone, respondentSetEmail } from "../../redux/actions";
import { getValidationResults } from "../../redux/thunks/validation";
import { VariableType } from "../../models/enums/VariableType";
import { VariableModel } from "../../models/VariableModel";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

interface Props extends ControlProps, DispatchProp {}

interface State {
    value: string;
    focus: boolean;
    answers: ChoiceModel[];
    input?: HTMLElement;
}

export class TextBoxControl extends BaseControl<Props, State> {
    input?: HTMLElement;

    maxResults = (props: Props) => getSettingNumber(props.question, "MaxResults");

    constructor(props: Props) {
        super(props);
        this.state = {
            value: "",
            focus: false,
            answers: undefined,
        };
    }

    getFilteredAnswers(state: State, props: Props, showAllAnswers?: boolean) {
        if (!showAllAnswers) {
            const minSearchLength = getSettingNumber(props.question, "MinSearchLength", 0);
            if (isNullOrEmpty(state.value) || state.value.length < minSearchLength) {
                return [];
            }
        }
        const { question } = props;
        const searchValue = state.value.trim().toLowerCase();
        let choiceList = props.sequencedVariables[0].choiceList.filter(
            (choice) => !isNullOrEmpty(choice.value),
        );
        if (!showAllAnswers) {
            switch (getSettingString(question, "SearchOption")) {
                case "Exact":
                    choiceList = choiceList.filter(
                        (choice) =>
                            this.getChoiceText(choice, props.language).trim().toLowerCase() ===
                            searchValue,
                    );
                    break;
                case "StartsWith":
                    choiceList = choiceList.filter((choice) =>
                        this.getChoiceText(choice, props.language)
                            .trim()
                            .toLowerCase()
                            .startsWith(searchValue),
                    );
                    break;
                default:
                    choiceList = choiceList.filter((choice) =>
                        this.getChoiceText(choice, props.language)
                            .trim()
                            .toLowerCase()
                            .includes(searchValue),
                    );
            }
        }
        choiceList = choiceList.sort((a, b) => {
            if (a === b) {
                return 0;
            }
            return a < b ? -1 : 1;
        });
        if (this.maxResults(props) != null) {
            return choiceList.slice(0, this.maxResults(props));
        }
        return choiceList;
    }

    getChoiceText(choice: ChoiceModel, lang?: string) {
        return removeHtmlTags(translate(choice, "TEXT", lang || this.props.language));
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        const variable = this.getQuestionVariable(question.variables);
        if (!this.hasMentions(props)) {
            switch (question.type) {
                case QuestionType.RespondentPhone:
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(props.phone),
                    );
                    break;
                case QuestionType.RespondentEmail:
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(props.email),
                    );
                    break;
                default:
                    break;
            }
        }
        this.updateState(props, false);
    }

    willUpdateMentions(question: QuestionModel, nextProps: Props) {
        super.willUpdateMentions(question, nextProps);
        this.updateState(nextProps, false);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.language !== this.props.language) {
            this.updateState(this.props);
        }
    }

    componentDidMount() {
        this.setState((prevState) => ({ ...prevState, input: this.input }));
    }

    hasAnswerWithValue(props: Props, value: string) {
        if (isNullOrEmpty(value)) {
            return false;
        }
        const { variables } = props.question;
        const [firstVariable] = variables;
        const { choiceList } = firstVariable;
        return choiceList.some((choice) => choice.value === value);
    }

    hasMentionsInChoices = (props: Props) => {
        const { question } = props;

        const [firstVariable] = question.variables;
        const { choiceList } = firstVariable;

        const hasMentions = this.hasMentions(props);
        const mentions = this.getMentions(props);
        const [firstMention] = mentions;

        const hasMentionInChoices =
            hasMentions &&
            choiceList
                .filter((choice) => !isNullOrEmpty(choice.value))
                .some((choice) => choice.value === firstMention.value);

        return hasMentionInChoices;
    };

    updateState(props: Props, shouldUpdateRespondentFields = true) {
        this.setState((prevState) => {
            const newState = { ...prevState };
            const mention = this.getMention(props);
            const softPromptCode = getSoftPromptCode(this.props.question);
            newState.value = "";
            switch (props.question.type) {
                case QuestionType.RespondentEmail:
                    newState.value = shouldUpdateRespondentFields
                        ? props.email
                        : mention?.value || prevState.value;
                    break;
                case QuestionType.RespondentPhone:
                    newState.value = shouldUpdateRespondentFields
                        ? props.phone
                        : mention?.value || prevState.value;
                    break;
                case QuestionType.OpenEndAnswer:
                    if (mention) {
                        newState.value = mention.openEnd;
                    }
                    break;
                case QuestionType.NumericAnswer: {
                    const hasAnswerWithValue =
                        mention && this.hasAnswerWithValue(props, mention.value);
                    const hasMentionsInChoices = this.hasMentionsInChoices(props);
                    const isChoiceVisible = mention && this.isChoiceVisible(mention.value, props);
                    if (
                        mention &&
                        !hasAnswerWithValue &&
                        (!hasMentionsInChoices || !isChoiceVisible)
                    ) {
                        const numeral = getNumeral(mention.value).value();
                        if (!isNullOrNaN(numeral)) {
                            newState.value = this.formatNumeric(numeral, props);
                        }
                    }
                    break;
                }
                default:
                    if (mention && mention.value !== softPromptCode) {
                        newState.value = mention.value;
                    }
                    break;
            }
            return newState;
        });
    }

    onKeyPress = (event: React.KeyboardEvent) => {
        if (this.isKeyPrintable(event.key) && this.hasMentionsInChoices(this.props)) {
            const { question } = this.props;
            const variable = this.getQuestionVariable(question.variables);
            this.setMentions(this.props, variable.name, (mention) => mention.clear());
        }
    };

    isKeyPrintable = (key: string) => key.length === 1;

    onTextChanged = (event) => {
        let { value } = event.target;
        const { maxLength } = event.target;
        const { question, language } = this.props;
        const caseMode = getSettingEnum(question, CaseMode, "CaseMode");

        const numericQuestionTypes = [QuestionType.NumericAnswer, QuestionType.RunningTotal];
        const isNumeric = numericQuestionTypes.includes(question.type);

        if (isNumeric) {
            const { NumberOfDecimals: nbDecimalDefine } = question.settings;
            const delimiter = decimalSeparator(language);
            const parts = value.split(delimiter);
            const [wholeNumber, decimalNumber] = parts;

            if (nbDecimalDefine === undefined || nbDecimalDefine === 0) {
                if (includes(value, delimiter)) {
                    return;
                }
                value = wholeNumber;
            } else if (
                includes(value, delimiter) &&
                decimalNumber &&
                decimalNumber.length > nbDecimalDefine
            ) {
                parts[1] = decimalNumber.substring(0, nbDecimalDefine);
                value = parts.join(delimiter);
            }

            if (includes(value, ".") && delimiter === ",") {
                value = value.replace(".", ",");
            }

            let invalidNumber;
            if (question.parent && question.parent.type === QuestionType.RunningTotal) {
                invalidNumber = value.match(/[^\d. ,-]|[.,]{2,}/) || includes(value, "-");
            } else {
                invalidNumber =
                    value.match(/[^\d. ,-]|[.,]{2,}/) ||
                    Array.from(value).find((char, index) => index > 0 && char === "-");
            }
            if (invalidNumber) {
                return;
            }
        }

        if (caseMode === CaseMode.AllCaps) {
            value = value.toUpperCase();
        } else if (caseMode === CaseMode.FirstLetterCaps) {
            value = value
                .split(" ")
                .map((v) => v.charAt(0).toUpperCase() + v.slice(1))
                .join(" ");
        }

        if (maxLength > -1 && value.length > maxLength) {
            value = value.substring(0, maxLength);
        }

        this.setState(
            (prevState) => {
                const newState = { ...prevState };
                newState.value = value;

                return newState;
            },
            () => this.updateAnswers(),
        );
    };

    formatNumeric(value: string | number, props: Props, cultureInvariant?: boolean) {
        const { question } = props;
        return formatNumeric(
            value,
            getSettingNumber(question, "NumberOfDecimals"),
            props.language,
            cultureInvariant,
            this.shouldFormat(question),
        );
    }

    shouldFormat(question: QuestionModel) {
        return this.isNumericAnswer(question) && shouldAutoFormat(question);
    }

    isNumericAnswer(question: QuestionModel) {
        return question.type === QuestionType.NumericAnswer;
    }

    updateAnswers() {
        const { question, language } = this.props;
        const variable = this.getQuestionVariable(question.variables);
        let { value } = this.state;

        const hasMentionInChoices = this.hasMentionsInChoices(this.props);
        if (isNullOrEmpty(value) && hasMentionInChoices) {
            return;
        }

        if (this.isNumericAnswer(question)) {
            const numeral = getNumeral(value, language).value();
            if (!isNullOrNaN(numeral)) {
                value = this.formatNumeric(numeral, this.props, true);
            }
        }

        if (question.type === QuestionType.OpenEndAnswer) {
            value = trim(value || "");
            const answerCode = getSettingString(question, "AnswerCode", "0");
            this.updateMention(value, variable, answerCode);
            return;
        }

        this.updateMention(value, variable);
    }

    updateMention = (value: string, variable: VariableModel, code?: string) => {
        const mention = this.getMentions(this.props, variable.name)?.find((m) =>
            this.mentionIsValid(m, this.props.question),
        );
        if (isNullOrEmpty(value)) {
            if (mention) {
                this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
            }
        } else if (mention?.value !== value) {
            this.setMentions(this.props, variable.name, (mentions) => {
                if (code) {
                    return mentions.set(code, value);
                }
                return mentions.set(value);
            });
        }
    };

    onTextBlur = () => {
        this.updateAnswers();
    };

    placeholder() {
        return "";
    }

    inputtype(question: QuestionModel) {
        switch (question.type) {
            case QuestionType.RespondentEmail:
            case QuestionType.EmailAddressAnswer:
                return "email";
            case QuestionType.RespondentPhone:
                return "tel";
            default:
                return "text";
        }
    }

    maxLength = (question: QuestionModel) => getSettingNumber(question, "MaxAnswerLength");

    minValue() {
        const min = super.minValue();
        return min != null ? min : Number.MIN_SAFE_INTEGER;
    }

    maxValue() {
        const max = super.maxValue();
        return max != null ? max : Number.MAX_SAFE_INTEGER;
    }

    displayValue = () => {
        // Use the mention to make sure text does not disappear whenever it matches a possible answer.
        const mentionValue = this.getMentionValue(this.props);

        // Check if the value is equal to the mention to let the user change the value.
        const isValueMentioned =
            this.state.value === mentionValue && this.hasAnswerWithValue(this.props, mentionValue);

        if (this.state.value === undefined || isValueMentioned) {
            return "";
        }
        return this.state.value;
    };

    textAlign = (question: QuestionModel) => {
        if (question.type === QuestionType.NumericAnswer) {
            return "right";
        }
        return undefined;
    };

    renderQuestion(question: QuestionModel) {
        const multiline =
            getSettingBoolean(question, "MultiLine") ||
            question.type === QuestionType.OpenEndAnswer;

        const lines = getSettingNumber(question, "Lines");

        return (
            <>
                <TextAnswerRenderer
                    {...getRendererProps(this.props)}
                    multiline={multiline}
                    inputType={this.inputtype(question)}
                    inputMode={question.type === QuestionType.NumericAnswer ? "decimal" : undefined}
                    placeholder={this.placeholder()}
                    inputRef={(r) => {
                        this.input = r as HTMLInputElement;
                    }}
                    min={this.minValue()}
                    max={this.maxValue()}
                    maxLength={this.maxLength(question)}
                    onBlur={this.onTextBlur}
                    onChange={this.onTextChanged}
                    onKeyPress={this.onKeyPress}
                    value={this.displayValue()}
                    disabled={this.props.isDisabled}
                    lines={lines}
                />
                <ControlContainer
                    controlType={ChoiceListControl}
                    {...getRendererProps(this.props)}
                />
            </>
        );
    }

    regexForValidationMask(mask: string) {
        let regex = "";
        const replacements = {
            A: "[A-Z]",
            Z: "[A-Z0-9]",
            9: "[0-9]",
            "*": ".",
        };
        Array.from(mask).forEach((c) => {
            regex += replacements[c] || `\\${c}`;
        });
        return `^${regex}$`;
    }

    validateFormat(question: QuestionModel, value: string) {
        const mask = getSettingString(question, "ValidationMask");
        if (isNullOrEmpty(mask)) {
            return true;
        }
        return new RegExp(this.regexForValidationMask(mask), "i").test(value);
    }

    shouldEnforceValidation(question: QuestionModel) {
        return (
            !hasSetting(question, "EnforceValidation") ||
            getSettingBoolean(question, "EnforceValidation")
        );
    }

    isAnswerComplete() {
        if (isNullOrEmpty(this.state.value) && this.hasMentions(this.props)) {
            const [mention] = this.getMentions(this.props);
            const variable = this.getQuestionVariable(this.props.sequencedVariables);
            const { choiceList = [] } = variable;
            if (choiceList.some((choice) => choice.value === mention.value)) {
                return true;
            }
        }
        return !isNullOrEmpty(trim(this.state.value));
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        this.updateAnswers();
        const { question, useClientValidation, dispatch } = this.props;
        const { value } = this.state;

        const validation = new ValidationBuilder();
        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            if (question.type === QuestionType.RespondentPhone) {
                dispatch(respondentSetPhone(value));
            } else if (question.type === QuestionType.RespondentEmail) {
                dispatch(respondentSetEmail(value));
            }
            return validation.build();
        }

        const variable = question.variables.find((v) => v.type !== VariableType.Computed);
        const hasSelectedAnswer = this.hasAnswerWithValue(this.props, value);
        let result = super.validate(options);

        if (result.isValid) {
            if (!isNullOrEmpty(value) && !hasSelectedAnswer) {
                switch (question.type) {
                    case QuestionType.OpenEndAnswer:
                        result = this.validateOpenEnd(value, question);
                        if (!result.isValid) {
                            return result;
                        }
                        break;
                    case QuestionType.RespondentPhone:
                        if (
                            this.shouldEnforceValidation(question) &&
                            !PhoneRegularExpression.test(value)
                        ) {
                            return validation.fail(variable.name, "InvalidAnswer").build();
                        }
                        this.props.dispatch(respondentSetPhone(value));
                        break;
                    case QuestionType.RespondentEmail:
                        if (
                            this.shouldEnforceValidation(question) &&
                            !EmailRegularExpression.test(value)
                        ) {
                            return validation.fail(variable.name, "InvalidEmailAddress").build();
                        }
                        this.props.dispatch(respondentSetEmail(value));
                        break;
                    case QuestionType.EmailAddressAnswer:
                        if (
                            this.shouldEnforceValidation(question) &&
                            !EmailRegularExpression.test(value)
                        ) {
                            return validation.fail(variable.name, "InvalidEmailAddress").build();
                        }
                        break;
                    case QuestionType.TextAnswer:
                        if (!this.validateFormat(question, value)) {
                            return validation.fail(variable.name, "InvalidTextFormat").build();
                        }
                        break;
                    case QuestionType.NumericAnswer:
                        return getValidationResults([question]);
                    default:
                        break;
                }
            }
        }
        return result;
    }
}
